import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import i18n from 'live/services/i18n'
import styled from 'styled-components'
import { HotTable, HotColumn } from '@handsontable/react'
import { countriesOptions } from './countriesOptions'
import LiveSelect from 'live/components/forms/ui/select'
import ColorPicker from 'live/components/ui/form/color-picker'
import UploadButton from './UploadButton'
import { Asterisk } from '../common/components'

interface IContentProps {
    assetName: string
    secondaryValue: string
    tableDataArray?: string[][]
    showErrors: boolean
    isEdit: boolean
    skuColor?: string
    setAssetName: (name: string) => void
    setSecondaryValue: (name: string) => void
    setTableDataArray?: (lines: string[][]) => void
    setSkuColor?: (color: string) => void
    contentType: 'product' | 'sku' | 'factory'
    currentProductImageId?: string
    setImageFile?: Dispatch<SetStateAction<FileType>>
}
const rowHeight = 25
const marginBottom = 5
export function DialogContent({
    assetName,
    secondaryValue,
    tableDataArray,
    skuColor,
    isEdit,
    setAssetName,
    setSecondaryValue,
    setTableDataArray,
    setSkuColor,
    showErrors,
    contentType,
    currentProductImageId,
    setImageFile
}: IContentProps): JSX.Element {
    const [countrySelectOptions, setCountrySelectOptions] = useState([])

    useEffect(() => {
        const locale = Live?.settings && Live.settings.locale ? Live.settings.locale : 'pt_br'
        const newCountriesOptions = countriesOptions.map(option => ({
            ...option,
            value: option[locale],
            label: option[locale]
        }))
        setCountrySelectOptions(newCountriesOptions)
    }, [])

    function onChangeTable(changes: [number, string | number, string, string][]): void {
        if (changes) {
            const tableData = [...tableDataArray]
            changes.map(change => {
                const tableRow = change[0]
                const tableColumn = change[1]
                const newValue: string = change[3]
                tableData[tableRow][tableColumn] = newValue
            })
            setTableDataArray(tableData)
        }
    }

    const assetContents = {
        factory: {
            nameInput: 'factory',
            secondaryInput: 'country'
        },
        product: {
            nameInput: 'product',
            secondaryInput: 'id'
        },
        sku: {
            nameInput: 'sku',
            secondaryInput: 'id'
        }
    }

    return (
        <StyledDialogContent className={'dialogContent'}>
            <label htmlFor="dialogContent--assetNameInput" className={`dialogContent--assetNameLabel`}>
                {`${i18n('name')}`} <Asterisk />
            </label>
            <input
                onChange={event => setAssetName(event.target.value)}
                value={assetName}
                required={true}
                type="text"
                id="dialogContent--assetNameInput"
                className={`dialogContent--assetNameInput ${!assetName && showErrors ? 'hasError' : ''}`}
                name="dialogContent--assetNameInput"
            ></input>
            <label htmlFor="dialogContent--secondaryValue" className="dialogContent--sencondaryLabel">
                {i18n(assetContents[contentType].secondaryInput)}
                {assetContents[contentType].secondaryInput === 'country' ? <Asterisk /> : null}
            </label>
            {assetContents[contentType].secondaryInput === 'country' ? (
                <LiveSelect
                    name="countryName"
                    value={secondaryValue}
                    options={countrySelectOptions}
                    onChange={value => setSecondaryValue(value)}
                    className={`dialogContent--secondaryValue ${!secondaryValue && showErrors ? 'hasError' : ''}`}
                    clearable={false}
                    searchable={true}
                    placeholder={i18n('select a country')}
                ></LiveSelect>
            ) : (
                <input
                    onChange={event => setSecondaryValue(event.target.value)}
                    value={secondaryValue}
                    required={false}
                    id="dialogContent--secondaryValue"
                    type="text"
                    className={`dialogContent--secondaryValue ${!secondaryValue && showErrors ? 'hasError' : ''}`}
                    name="dialogContent--secondaryValue"
                ></input>
            )}

            {contentType === 'product' ? (
                <UploadButton currentProductImageId={currentProductImageId} setImageFile={setImageFile} />
            ) : null}

            {contentType === 'sku' ? (
                <>
                    <label htmlFor="dialogContent--assetNameInput" className={`dialogContent--assetNameLabel`}>
                        {`${i18n('color')}`}
                    </label>
                    <ColorPicker
                        value={skuColor}
                        onChange={color => {
                            setSkuColor(color)
                        }}
                        position={'left'}
                        disableAlpha={true}
                    />
                </>
            ) : null}

            {isEdit || contentType === 'sku' ? null : (
                <React.Fragment>
                    <HotTable
                        data={tableDataArray}
                        height={(tableDataArray.length + 1) * rowHeight + marginBottom}
                        afterChange={change => onChangeTable(change)}
                        stretchH={'all'}
                        rowHeights={rowHeight}
                    >
                        <HotColumn title={contentType === 'product' ? i18n('sku name') : i18n('lines')} />
                        {contentType === 'product' ? <HotColumn title={'id'} /> : null}
                    </HotTable>
                    <button onClick={() => setTableDataArray([...tableDataArray, ['', '']])}>{i18n('add row')}</button>
                </React.Fragment>
            )}
        </StyledDialogContent>
    )
}

const StyledDialogContent = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    .dialogContent {
        &--asterisk {
            color: red;
        }
        &--assetNameLabel,
        &--sencondaryLabel {
            text-transform: capitalize;
        }
        &--secondaryValue,
        &--assetNameInput {
            border: 1px solid ${({ theme }) => theme['gray-300']};
            &.hasError {
                border: 1px solid ${({ theme }) => theme['red-500']};
            }
        }
        &--assetNameInput,
        &--secondaryValue {
            height: 30px;
            margin-bottom: 20px;
        }
        &--iconContent {
            display: flex;
            align-items: center;
            padding-bottom: 20px;

            gap: 30px;
        }
    }

    .hot-container {
        width: 100%;
        flex-grow: 1;
        /* overflow: hidden; */
    }
`
